import VueRouter from "vue-router";
import { Dictionary, Location } from "vue-router/types/router";

import { loadSelectedProject } from "../stores/site/effects/initialiseSelectedProject";
import { checkForServiceWorkerUpdate, reloadIfUpdateIsAvailable } from "../service-worker-window";
import { ENVIRONMENT } from "../util/environment";
import { userStore } from "../stores/user/store";
import { DashboardRoutes } from "./routes/dashboard";
import { ProjectsRoutes } from "./routes/projects";
import { AccountRoutes } from "./routes/account";
import { PublicRoutes } from "./routes/public";
import { ShareRoutes } from "./routes/share";
import { SitesRoutes } from "./routes/sites";
import { MapRoutes } from "./routes/map";
import hasBetaFeature from "../stores/user/actions/hasBetaFeature";
import Feature from "../stores/user/Feature";
import { checkUser, logNavigation } from "./guards";
import { sharedSiteRedirect } from "./shared-site-redirect";
import ROUTES from "./routes";

const router = new VueRouter({
  base: ENVIRONMENT.BASE_URL,
  mode: "history",
  routes: [
    ...DashboardRoutes,
    ...SitesRoutes,
    ...AccountRoutes,
    ...PublicRoutes,
    ...MapRoutes,
    ...ShareRoutes,
    ...ProjectsRoutes,
    {
      path: "*",
      redirect: "/map",
    },
  ],
});

export enum REDIRECT_OPTIONS {
  SHARED_SITE = "sharedSite",
}

router.beforeEach(async (to, from, next) => {
  const { user } = userStore;
  // Redirect legacy public map URL to new share URL
  if (to.name === ROUTES.PUBLIC_MAP) {
    if (to.query.siteId) {
      return next({ path: `/${ROUTES.SHARE}/${to.query.siteId}` });
    } else {
      return next({ name: ROUTES.MAP });
    }
  }
  const userCheck = await checkUser({ user, routeName: to.name });
  let redirectURL: Location | undefined;

  checkForServiceWorkerUpdate();

  if (!userCheck.authenticated) {
    next({
      name: ROUTES.LOGOUT,
      params: { error: userCheck.error || "" },
    });
    return;
  }

  if (userCheck?.attemptRedirect === REDIRECT_OPTIONS.SHARED_SITE) {
    redirectURL = await sharedSiteRedirect({ routeName: to.name || "" });
  }
  if (redirectURL) {
    logNavigation(to.name || "", redirectURL.name);
    return next(redirectURL);
  }

  if (hasBetaFeature(Feature.projectsNavBeta)) {
    await loadSelectedProject();
  }

  next();
});

router.afterEach((to, from) => {
  logNavigation(to.name || "");
  if (from.fullPath !== "/" && to.fullPath.split("/")[1] !== from.fullPath.split("/")[1]) {
    reloadIfUpdateIsAvailable();
  }
});

type WithoutRefreshParams = {
  name?: string;
  params?: Dictionary<string>;
  query?: Dictionary<string>;
};

const redirectWithoutMapRefresh = ({ name, params, query }: WithoutRefreshParams) => {
  const { href } = router.resolve({
    name,
    params,
    query,
  });
  window.history.pushState({}, "", href);
};

export { router, redirectWithoutMapRefresh };
